import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useWeekIndex } from "@/contexts/WeekIndexProvider";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useEffectiveWeekStartDay } from "@/hooks/useEffectiveWeekStartDay";
import { useJobs } from "@/hooks/useJobs";
import type { SystemAutoCompleteOption } from "@/ui/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { Job, Nurse, NurseRole } from "@/utils/models";
import { toIsoDate } from "@/utils/scheduleWeeks";
import { daySchedulePath } from "@/utils/staffingRoutes";
import {
	buildMonthlyRows,
	buildUnassignedJob,
	DAYS_IN_WEEK,
	deriveShiftWindows,
	type MonthlyDayBoundary,
	type MonthlyWeekRow,
	monthlyAnchorOf,
	monthlyWindowStart,
	StaffingTotalsMode,
} from "./monthlyJobUtils";
import { type MonthlyViewConfig, readMonthlyViewConfig, resolveVisibleJobs, writeLastStaffingView, writeMonthlyViewConfig } from "./monthlyViewPreferences";
import { useMonthlyDays } from "./useMonthlyDays";

/** The Monthly view shows six weeks and its arrows jump six weeks at a time (g-cal style). */
export const MONTHLY_WEEKS_TO_SHOW = 6;

export type MonthlySidebarSelection = {
	date: DateTime;
	/** When set, the sidebar opens focused on this job (a job pill was clicked, not the whole cell). */
	focusJobId?: string;
};

export const useMonthlyJobViewState = () => {
	const { getWeekIndexByDate } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { date: dateParam } = useParams<{ date?: string }>();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const enabledJobs = useJobs();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const departmentId = chosenDepartment.department_id;

	// The department's staffing-day boundary, resolved ONCE and threaded down as one value. It decides
	// where a window sorts, what instant a requirement-only row is anchored on, and which band a window
	// is painted in — never which day a shift belongs to (the backend already answered that; see the
	// invariant on `MonthlyDayData`).
	const dayBoundary = useMemo<MonthlyDayBoundary>(
		() => ({ dayStart: chosenDepartment.dayStart, graceMinutes: chosenDepartment.scheduled_shift_delta_minutes }),
		[chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const today = useMemo(() => DateTime.now().setZone(timezone).startOf("day"), [timezone]);
	const weekStartDay = useEffectiveWeekStartDay();

	// Remember that the scheduler last used the Monthly view (drives the weekly/monthly landing choice).
	useEffect(() => {
		writeLastStaffingView("monthly");
	}, []);

	// --- Config (totals mode + which jobs are shown), persisted per user + department ---
	const [config, setConfig] = useState<MonthlyViewConfig>(() => readMonthlyViewConfig(departmentId));
	useEffect(() => {
		setConfig(readMonthlyViewConfig(departmentId));
	}, [departmentId]);

	const visibleJobs = useMemo(() => resolveVisibleJobs(enabledJobs, config.visibleJobIds), [enabledJobs, config.visibleJobIds]);

	/**
	 * What the AGGREGATION counts under, as opposed to what the view settings and the job filter OFFER.
	 *
	 * The two differ by exactly one entry: the unassigned bucket, which every shift carrying no `jobId`
	 * lands in. It has to be a job to the aggregation (so it gets a pill, a window split, a drawer group
	 * and a chip for free instead of a branch in each) and must NOT be a job to the settings panel or the
	 * role/job selector, which configure the department's real job taxonomy. Empty days cost nothing:
	 * every aggregation drops a zero-count job.
	 */
	const countedJobs = useMemo(() => [...visibleJobs, buildUnassignedJob(t("monthly.view.job.unassigned"))], [visibleJobs, t]);

	const persistConfig = useCallback(
		(next: MonthlyViewConfig) => {
			setConfig(next);
			writeMonthlyViewConfig(departmentId, next);
		},
		[departmentId],
	);

	const setTotalsMode = useCallback(
		(totalsMode: StaffingTotalsMode) => {
			persistConfig({ ...config, totalsMode });
			track("monthly_view_totals_mode_changed", { totals_mode: totalsMode });
		},
		[config, persistConfig, track],
	);

	const setVisibleJobIds = useCallback(
		(jobIds: string[]) => {
			persistConfig({ ...config, visibleJobIds: jobIds });
			track("monthly_view_visible_jobs_changed", { job_count: jobIds.length });
		},
		[config, persistConfig, track],
	);

	// --- Filters ---
	// Role and job selections are held in PARALLEL (the `RoleOrJobSelector` contract): the flag decides
	// which one is on screen, and keeping both means toggling it never loses the inactive selection.
	const [windowKey, setWindowKeyState] = useState<string | null>(null);
	const [roleFilter, setRoleFilterState] = useState<(NurseRole | "all")[]>(["all"]);
	const [jobFilterIds, setJobFilterIdsState] = useState<string[]>([]);
	const [locationFilter, setLocationFilter] = useState<string[]>([]);
	const [searchedNurse, setSearchedNurse] = useState<SystemAutoCompleteOption<Nurse> | null>(null);

	const setWindowKey = useCallback(
		(nextWindowKey: string | null) => {
			setWindowKeyState(nextWindowKey);
			track("monthly_view_shift_filter_changed", { window_key: nextWindowKey });
		},
		[track],
	);

	const setRoleFilter = useCallback(
		(roles: (NurseRole | "all")[]) => {
			setRoleFilterState(roles.length === 0 ? ["all"] : roles);
			track("monthly_view_role_filter_changed", { role_count: roles.filter((role) => role !== "all").length });
		},
		[track],
	);

	const setJobFilterIds = useCallback(
		(jobIds: string[]) => {
			setJobFilterIdsState(jobIds);
			track("monthly_view_job_filter_changed", { job_count: jobIds.length });
		},
		[track],
	);

	// --- Window / navigation ---
	// The window is anchored on the Sunday of the week its first row starts in, as the provider's weeks are, and its
	// first day is that Sunday plus the week-start offset — the day the URL names. A change of week start therefore
	// keeps the window on the same weeks.
	const anchorFromUrlOrToday = useCallback((): DateTime => {
		// Parsed IN the department zone: parsing at browser-local midnight and converting can shift a `yyyy-MM-dd`
		// back a day for a browser ahead of the department, landing on the wrong week. A hand-edited mid-week date
		// lands on the window that contains it.
		const parsed = dateParam ? DateTime.fromISO(dateParam, { zone: timezone }) : undefined;
		return monthlyAnchorOf(parsed?.isValid ? parsed : today, weekStartDay);
	}, [dateParam, timezone, today, weekStartDay]);
	const [requestedAnchor, setRequestedAnchor] = useState<DateTime>(anchorFromUrlOrToday);

	// Only a department switch goes back to the URL's (or today's) window: the URL writes below must not.
	const anchoredDepartmentRef = useRef(departmentId);
	useEffect(() => {
		if (anchoredDepartmentRef.current === departmentId) return;
		anchoredDepartmentRef.current = departmentId;
		setRequestedAnchor(anchorFromUrlOrToday());
	}, [departmentId, anchorFromUrlOrToday]);

	const requestedStart = monthlyWindowStart(requestedAnchor, weekStartDay);
	const { daysByDate, range, isLoading, isError, refetch } = useMonthlyDays(
		requestedStart,
		requestedStart.plus({ days: MONTHLY_WEEKS_TO_SHOW * DAYS_IN_WEEK - 1 }),
		weekStartDay,
	);

	// In range when its first row is: the same rule that drops the rows starting after the last day.
	const isAnchorInRange = useCallback(
		(candidate: DateTime) =>
			range !== undefined &&
			toIsoDate(candidate) >= toIsoDate(range.firstAnchor) &&
			toIsoDate(monthlyWindowStart(candidate, weekStartDay)) <= toIsoDate(range.lastDay),
		[range, weekStartDay],
	);

	// A window outside the range — a hand-edited URL, or a date past the schedule — opens on today's window, or on
	// the nearer end when today is outside it too.
	const anchor = useMemo(() => {
		if (!range || isAnchorInRange(requestedAnchor)) return requestedAnchor;
		const todaysAnchor = monthlyAnchorOf(today, weekStartDay);
		if (isAnchorInRange(todaysAnchor)) return todaysAnchor;
		return toIsoDate(requestedAnchor) < toIsoDate(range.firstAnchor) ? range.firstAnchor : monthlyAnchorOf(range.lastDay, weekStartDay);
	}, [range, isAnchorInRange, requestedAnchor, today, weekStartDay]);
	useEffect(() => {
		if (toIsoDate(anchor) !== toIsoDate(requestedAnchor)) setRequestedAnchor(anchor);
	}, [anchor, requestedAnchor]);

	const currentTimeFrameStartDate = useMemo(() => monthlyWindowStart(anchor, weekStartDay), [anchor, weekStartDay]);

	// Resolved ONCE here rather than in the grid: the shift-time windows are derived from the same 42 days the grid
	// renders, and resolving them twice would risk the chip row and the cells disagreeing.
	const visibleWeeks = useMemo<MonthlyWeekRow[]>(
		() =>
			range
				? buildMonthlyRows({
						windowStart: currentTimeFrameStartDate,
						rowCount: MONTHLY_WEEKS_TO_SHOW,
						firstDay: range.firstDay,
						lastDay: range.lastDay,
						daysByDate,
					})
				: [],
		[range, currentTimeFrameStartDate, daysByDate],
	);

	const currentTimeFrameEndDate = useMemo(
		// Derived from the rows so the label can never disagree with how many are on screen.
		() => visibleWeeks.at(-1)?.weekStart.plus({ days: DAYS_IN_WEEK - 1 }) ?? currentTimeFrameStartDate.plus({ days: MONTHLY_WEEKS_TO_SHOW * DAYS_IN_WEEK - 1 }),
		[visibleWeeks, currentTimeFrameStartDate],
	);

	// The Weekly view, Export and the view switcher still read the shared week cursor, so it follows the window.
	// Keyed on the window AND its week's position: it re-syncs when the loaded weeks shift, but never fights the
	// switcher, which moves the cursor itself on the way to the Weekly view.
	const syncedCursorRef = useRef<string | null>(null);
	useEffect(() => {
		const cursor = getWeekIndexByDate(currentTimeFrameStartDate);
		const key = `${toIsoDate(currentTimeFrameStartDate)}:${cursor}`;
		if (cursor === -1 || syncedCursorRef.current === key) return;
		syncedCursorRef.current = key;
		setWeekIndex(cursor);
	}, [currentTimeFrameStartDate, getWeekIndexByDate, setWeekIndex]);

	const isPastDay = useCallback((date: DateTime) => date.startOf("day") < today, [today]);

	// The department's real shift-time windows, counted across the WHOLE visible span so the chip row (and
	// every cell's row order) stays put as the user pages within a month.
	const shiftWindows = useMemo(
		() =>
			deriveShiftWindows({
				days: visibleWeeks.flatMap((week) => week.days.map((day) => ({ day: day.mergedDay, showActual: isPastDay(day.date) }))),
				visibleJobs: countedJobs,
				locations: chosenDepartment.locations,
				referenceDate: currentTimeFrameStartDate,
				dayBoundary,
			}),
		[visibleWeeks, countedJobs, chosenDepartment.locations, currentTimeFrameStartDate, isPastDay, dayBoundary],
	);

	/**
	 * The window filter key that is actually in force — the raw selection, but only while the derived set
	 * still offers it.
	 *
	 * The two can part company on any input the derivation depends on: hiding a job, changing the
	 * location filter or simply paging to six weeks the department staffed differently can retire the
	 * selected window. The raw key then named nothing: `MonthlyFilterBar` fell back to showing "All"
	 * (`selectedFilters` can't find it) while `passesFilters` kept excluding every shift that couldn't
	 * resolve to it — a grid emptied by a filter the chip row said was off. ONE derived key drives both
	 * the display and the aggregation, so that disagreement is not representable. Deliberately derived
	 * rather than reset in an effect: an effect would have to run after a render that already showed the
	 * empty grid, and the user's original choice survives if the window comes back.
	 */
	const effectiveWindowKey = useMemo(
		() => (windowKey !== null && shiftWindows.some((window) => window.key === windowKey) ? windowKey : null),
		[windowKey, shiftWindows],
	);

	// Keep the URL pointed at the visible window's first day, without adding history entries.
	//
	// `navigate(..., { replace: true })` rather than `window.history.replaceState`: the raw History API
	// bypasses React Router, desyncing its history stack, which broke browser Back after the sidebar
	// pushed a `/nurse/:id` route. `replace` still collapses arrow-paging into ONE entry, so the original
	// intent survives. Comparing against the ROUTER's `pathname` (not `window.location`) makes the effect
	// self-terminating: the write updates `pathname`, the guard then matches, and there is no second write.
	useEffect(() => {
		if (!range) return;
		const expected = `/${departmentId}/staffing/monthly/${toIsoDate(currentTimeFrameStartDate)}`;
		if (pathname !== expected) {
			navigate(expected, { replace: true });
		}
	}, [currentTimeFrameStartDate, departmentId, range, pathname, navigate]);

	const isPrevDisabled = !range || toIsoDate(anchor) <= toIsoDate(range.firstAnchor);
	// Off once the window shows the last day: a row's trailing days past it are empty cells, not another page.
	const isNextDisabled = !range || toIsoDate(currentTimeFrameEndDate) >= toIsoDate(range.lastDay);

	const handlePrev = useCallback(() => {
		if (!range) return;
		const previous = anchor.minus({ weeks: MONTHLY_WEEKS_TO_SHOW });
		setRequestedAnchor(toIsoDate(previous) < toIsoDate(range.firstAnchor) ? range.firstAnchor : previous);
		track("monthly_view_paged", { direction: "back" });
	}, [range, anchor, track]);

	const handleNext = useCallback(() => {
		if (isNextDisabled) return;
		setRequestedAnchor(anchor.plus({ weeks: MONTHLY_WEEKS_TO_SHOW }));
		track("monthly_view_paged", { direction: "forward" });
	}, [isNextDisabled, anchor, track]);

	const handleRetry = useCallback(() => {
		track("monthly_view_retry_clicked", { all_weeks_failed: range === undefined });
		refetch();
	}, [range, refetch, track]);

	const handleToday = useCallback(() => {
		const todaysAnchor = monthlyAnchorOf(today, weekStartDay);
		if (isAnchorInRange(todaysAnchor)) setRequestedAnchor(todaysAnchor);
		track("monthly_view_today_clicked");
	}, [today, weekStartDay, isAnchorInRange, track]);

	const handleDatePickerSelected = useCallback(
		(selected: DateTime) => {
			const selectedAnchor = monthlyAnchorOf(selected, weekStartDay);
			if (isAnchorInRange(selectedAnchor)) setRequestedAnchor(selectedAnchor);
			track("monthly_view_date_picker_selected", { selected_date: selected.toFormat("yyyy-MM-dd") });
		},
		[weekStartDay, isAnchorInRange, track],
	);

	// The earliest PICKABLE day is the first window's start: with a week that starts after Sunday, the days before
	// it belong to a window whose anchor week is outside the range.
	const minDate = range && monthlyWindowStart(range.firstAnchor, weekStartDay);
	const maxDate = range?.lastDay;

	// Click the week label -> jump to the Weekly view for that exact visible week.
	const handleWeekNumberClick = useCallback(
		(weekStart: DateTime) => {
			const formatted = toIsoDate(weekStart);
			track("monthly_view_week_number_clicked", { week_start: formatted });
			navigate(`/${departmentId}/staffing/${formatted}`);
		},
		[departmentId, navigate, track],
	);

	/**
	 * Jump to the full Day view for one day — the tile's arrow and the day drawer's own jump, sharing ONE
	 * rule so the two entry points can never land somewhere different.
	 *
	 * Always THIS day, never `StaffingDateTypeSelector`'s "today if today is in view": the user pointed at
	 * a specific day and the jump has to honour it. `source` is the only thing that differs between the
	 * two callers, and it is an analytics property rather than a branch.
	 */
	const handleOpenDayView = useCallback(
		(date: DateTime, source: "cell" | "sidebar") => {
			const formatted = date.toFormat("yyyy-MM-dd");
			track("monthly_view_day_view_opened", { date: formatted, source });
			navigate(daySchedulePath(departmentId, formatted));
		},
		[departmentId, navigate, track],
	);

	// --- Sidebar ---
	const [sidebarSelection, setSidebarSelection] = useState<MonthlySidebarSelection | null>(null);
	const openSidebar = useCallback(
		(selection: MonthlySidebarSelection) => {
			setSidebarSelection(selection);
			track("monthly_view_day_sidebar_opened", {
				date: selection.date.toFormat("yyyy-MM-dd"),
				focus_job_id: selection.focusJobId ?? null,
			});
		},
		[track],
	);
	/** Whether a day is in the schedule, so the staffing panels can act on it — see `MonthlyVisibleDay.isInSchedule`. */
	const isDateInSchedule = useCallback(
		(date: DateTime) => range !== undefined && toIsoDate(date) >= toIsoDate(range.firstDay) && toIsoDate(date) <= toIsoDate(range.lastDay),
		[range],
	);
	const closeSidebar = useCallback(() => setSidebarSelection(null), []);

	return {
		// data
		/** Each day the view has, by ISO date — the sidebar reads its day from here. */
		daysByDate,
		isDateInSchedule,
		/** Whether the department has a schedule to show; undefined while that is still unknown: loading, or every week failed. */
		hasSchedule: range === undefined ? (isLoading || isError ? undefined : false) : true,
		isLoading,
		isError,
		handleRetry,
		enabledJobs,
		visibleJobs,
		/** `visibleJobs` plus the explicit unassigned bucket — what every aggregation counts under. */
		countedJobs,
		locations: chosenDepartment.locations,
		/** The department's staffing-day boundary: ordering, anchoring and banding only. */
		dayBoundary,
		timezone,
		today,
		isPastDay,
		departmentId,
		// window + navigation
		/** The shared week cursor, which the Weekly view, Export and the view switcher still read. */
		weekIndex,
		visibleWeeks,
		/** The department's derived shift-time windows for the whole visible span. */
		shiftWindows,
		setWeekIndex,
		currentTimeFrameStartDate,
		currentTimeFrameEndDate,
		isPrevDisabled,
		isNextDisabled,
		handlePrev,
		handleNext,
		handleToday,
		handleDatePickerSelected,
		handleWeekNumberClick,
		handleOpenDayView,
		minDate,
		maxDate,
		// filters
		/** The key in force — a selection the derived set no longer offers reads as "All" everywhere. */
		windowKey: effectiveWindowKey,
		setWindowKey,
		roleFilter,
		setRoleFilter,
		jobFilterIds,
		setJobFilterIds,
		locationFilter,
		setLocationFilter,
		searchedNurse,
		setSearchedNurse,
		// config
		totalsMode: config.totalsMode,
		setTotalsMode,
		savedVisibleJobIds: config.visibleJobIds,
		setVisibleJobIds,
		// sidebar
		sidebarSelection,
		openSidebar,
		closeSidebar,
	};
};

export type MonthlyJobViewState = ReturnType<typeof useMonthlyJobViewState>;

/** Convenience: the enabled-jobs list as a lookup for name/color resolution. */
export const indexJobsById = (jobs: Job[]): Map<string, Job> => new Map(jobs.map((job) => [job.id, job]));

// Re-exported so the view layer imports the mode enum from one place alongside the state.
export { StaffingTotalsMode };
